import React, { useRef, useState, useEffect, ReactElement, MutableRefObject, ChangeEvent } from 'react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Measurement, MeasurementDiagram } from '../../../../../types/entity.types';

import { getClassNames, fileToBase64 } from '../../../../../utils/util';
import { UPCOMING, ARCHIVED } from '../../../../../utils/constants';

import FilePicker from '../../../../common/input/file-picker/FilePicker';
import NumberPicker from '../../../../common/input/number-picker/NumberPicker';

import { ReactComponent as UploadIcon } from '../../../../../resources/icons/upload.svg';

import './accountability-measurements.scss';
import '../accountability.scss';

type Props = {
  status: string;
  measurement?: Measurement;
  updateMeasurement: (key: keyof Measurement, value: MeasurementDiagram | string | undefined) => void;
};

const AccountabilityMeasurements = ({ status, measurement, updateMeasurement }: Props): ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [showUploadInput, setShowUploadInput] = useState<boolean>(false);

  const fileRef: MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  useEffect((): void => {
    if (measurement?.measurementDiagram) {
      setShowUploadInput(true);
    }
  }, [measurement]);

  const handleDefaultClick = (): void => {
    updateMeasurement('measurementDiagram', undefined);
    setShowUploadInput(false);
  };

  const handleFileInputClick = (): void => {
    if (status !== ARCHIVED) {
      fileRef.current?.click();
    }
  };

  const handleFileChange = async ({ target }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!target.files) return;

    const file: File = target.files[0] as File;

    const diagram: MeasurementDiagram = {
      name: file.name,
      base64String: await fileToBase64(file)
    };

    updateMeasurement('measurementDiagram', diagram);
  };

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-measurements"
    >
      <div className="accountability__section__header">
        <p>MEASUREMENTS</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={(): void => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={(): void => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && (
        <div className="accountability-measurements">
          <div className="accountability-measurements__line-one">
            <p>Measurements will be taken every</p>
            <NumberPicker
              value={measurement?.measurementFrequency ?? ''}
              onChange={(value: string | number): void => updateMeasurement('measurementFrequency', value.toString())}
              disabled={status !== UPCOMING}
              hideArrows
            />
            <p>days, starting on Day</p>
            <NumberPicker
              value={measurement?.measurementStartDay ?? ''}
              onChange={(value: string | number): void => updateMeasurement('measurementStartDay', value.toString())}
              disabled={status !== UPCOMING}
              hideArrows
            />
          </div>
          <div className="accountability-measurements__line-two">
            <p>Measurements diagram:</p>
            <button
              className={getClassNames('accountability-measurements__toggle', {
                selected: !showUploadInput
              })}
              onClick={handleDefaultClick}
              disabled={status === ARCHIVED}
            >
              Default
            </button>
            <button
              className={getClassNames('accountability-measurements__toggle', {
                selected: showUploadInput
              })}
              onClick={(): void => setShowUploadInput(true)}
              disabled={status === ARCHIVED}
            >
              Upload
            </button>
            {showUploadInput && (
              <FilePicker
                triggerElement={
                  <div
                    className={getClassNames('accountability-measurements__file-input', {
                      disabled: status === ARCHIVED
                    })}
                  >
                    <p>{measurement?.measurementDiagram?.name}</p>
                    {status !== ARCHIVED && <UploadIcon onClick={handleFileInputClick} data-testid="upload-icon" />}
                  </div>
                }
                fileRef={fileRef}
                onChange={handleFileChange}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountabilityMeasurements;
